<template>
  <div class="vehicle-selector">
    <template v-if="isVehicleSearchVisible">
      <!-- REG NUMBER -->
      <form-row
        id="reg-number"
        :label="title || `Your ${vehicleTypeLabel} registration`"
        :question="`Do you know the registration number of the ${vehicleTypeLabel}?`"
        :is-vertical-splitted="false"
        :error="formObserver.validated"
      >
        <validation-provider
          v-slot="{ errors, failed }"
          :rules="regNumberRules"
          :vid="`${stepName}_vehicle-registration-number`"
          name="Registration Number"
          tag="div"
        >
          <v-row
            :class="{ 'flex-column': $vuetify.breakpoint.xs }"
            class="d-flex"
            dense
          >
            <v-col
              :style="{ 'max-width': $vuetify.breakpoint.xs ? 'unset' : '266px' }"
              class="flex-grow-1 mr-3 mr-sm-6"
            >
              <qz-text-field
                v-model="regNumber"
                :error="failed"
                :maxlength="inputCharsLimiters.REG_NUMBER_MAX"
                :placeholder="vehiclePlaceholder"
                class="input-vehicle-search"
                data-test="vehicle_registration_number"
                filled
                @keydown="onRegNumberKeyDown"
                @keyup.enter="!failed && searchByReg(regNumber)"
              />
            </v-col>
            <v-col>
              <qz-btn
                :block="$vuetify.breakpoint.xs"
                :disabled="isLoading"
                :height="!$vuetify.breakpoint.xs ? '100%' : null"
                :loading="isLoading"
                :x-large="$vuetify.breakpoint.xs"
                class="btn btn-find-vehicle"
                color="navy-light"
                data-test="find_vehicle_btn"
                light
                rounded
                @click.prevent="!failed && searchByReg(regNumber)"
              >
                <span>Find my {{ vehicleTypeLabel }}</span>
                <v-icon
                  dark
                  right
                  size="22"
                >
                  mdi-magnify
                </v-icon>
                <template #loader>
                  <span class="custom-loader">
                    <v-icon light>mdi-cached</v-icon>
                  </span>
                </template>
              </qz-btn>
            </v-col>
          </v-row>
          <div
            v-if="failed"
            class="error--text mt-3"
          >
            <p>{{ errors[0] }}</p>
          </div>
        </validation-provider>
        <template
          v-if="helpText"
          #helpText
        >
          <div class="d-flex align-start">
            <component
              :is="helpText.collapsible ? 'show-more' : 'div'"
              :open-text="helpText.collapsible ? (helpText.openerText || 'Show More') : false"
              :hide-text="helpText.collapsible ? (helpText.closerText || helpText.openerText || 'Hide') : false"
            >
              <div v-html="helpTextValue" />
            </component>
          </div>
        </template>
      </form-row>

      <!-- NO REGISTRATION -->
      <form-row
        id="no-registration"
        :is-vertical-splitted="false"
      >
        <checkbox-square
          v-model="isManual"
          v-add-zuko-tag
          data-test="no_vehicle_reg"
          name="no-vehicle-reg"
        >
          I don't know my registration
        </checkbox-square>
      </form-row>

      <!-- MANUAL VEHICLE CHOOSE -->
      <form-row
        v-if="isManual"
        :is-vertical-splitted="false"
      >
        <form-section-highlight>
          <!-- MAKE -->
          <form-row
            :question="makeTitle"
            :help-text="constants.HELP_TEXT"
            :is-vertical-splitted="false"
          >
            <qz-autocomplete
              ref="vehicleMake"
              v-model="vehicleSearch.make.value"
              :items="vehicleSearch.make.items"
              :placeholder="getPlaceholderResponsiveText('Please select make', isMobile)"
              :update-search="onVehicleMakeSearch"
              data-test="vehicle_make"
              is-init-search
              item-text="make_description"
              item-value="make_key"
              @change="handleChangeMake"
            />
          </form-row>
          <!-- MODEL -->
          <form-row
            v-if="showModelSelect"
            :question="constants.MODEL_TITLE"
            :is-vertical-splitted="false"
          >
            <qz-autocomplete
              ref="vehicleModel"
              v-model="vehicleSearch.model.value"
              :items="vehicleSearch.model.items"
              :placeholder="getPlaceholderResponsiveText(constants.MODEL_PLACEHOLDER, isMobile)"
              data-test="vehicle_model"
              item-text="label"
              item-value="id"
              @change="handleChangeModel"
            />
          </form-row>
          <!-- YEAR -->
          <form-row
            v-if="showYearSelect"
            question="What year was it registered?"
            :is-vertical-splitted="false"
          >
            <qz-autocomplete
              ref="vehicleYear"
              v-model="vehicleSearch.year.value"
              :items="vehicleSearch.year.items"
              :placeholder="getPlaceholderResponsiveText('Please select year', isMobile)"
              data-test="year_of_manufacture"
              item-text="label"
              item-value="id"
              @change="handleChangeYear"
            />
          </form-row>
          <!-- ENGINE -->
          <form-row
            v-if="showEngineSelect"
            id="vehicle_vehicle-size"
            question="Engine size and type"
            :is-vertical-splitted="false"
          >
            <qz-autocomplete
              ref="vehicleEngine"
              v-model="vehicleSearch.engine.value"
              :items="vehicleSearch.engine.items"
              :placeholder="getPlaceholderResponsiveText(constants.ENGINE_SIZE_PLACEHOLDER, isMobile)"
              data-test="engine_size"
              item-text="label"
              item-value="id"
              @change="handleChangeEngine"
            />
          </form-row>
          <!-- CONFIRM -->
          <form-row
            v-if="showConfirmVehicle"
            question="Confirm vehicle"
            :is-vertical-splitted="false"
          >
            <qz-autocomplete
              ref="vehicleConfirm"
              v-model="vehicleSearch.confirm.value"
              :items="vehicleSearch.confirm.items"
              :placeholder="getPlaceholderResponsiveText('Please select vehicle', isMobile)"
              data-test="vehicle_confirm"
              item-text="label"
              item-value="id"
              @change="onVehicleConfirmSelect"
            />
          </form-row>
        </form-section-highlight>
      </form-row>
    </template>

    <form-row
      v-if="isLoading && !showRegistrationNumber"
      :is-vertical-splitted="false"
    >
      <v-skeleton-loader type="heading" />
      <v-skeleton-loader
        class="mt-6 mb-3"
        height="90"
        type="card"
      />
      <v-skeleton-loader type="button" />
    </form-row>

    <!-- APPROVE VEHICLE -->
    <form-row
      v-if="showApprove"
      id="confirm-vehicle"
      :label="approveLabel"
      :question="constants.HELP_TEXT"
      :is-vertical-splitted="false"
    >
      <!-- APPROVE SINGLE VEHICLE -->
      <vehicle-selector-preview
        v-if="showSingleApprove"
        :key="vehicleSearch.results[0].confirmData.codeABI"
      >
        <icon
          slot="icon"
          :icon="vehicleIcon"
          size="56"
        />
        <p
          v-for="(line, i) in vehicleSearch.results[0].text"
          :key="line"
          class="wrap-words"
          :class="{'font-weight-bold': i === 0}"
        >
          {{ line }}
        </p>
      </vehicle-selector-preview>

      <!-- APPROVE FOR MULTIPLE VEHICLES -->
      <qz-select
        v-else
        v-model="vehicleSearch.chosenResult"
        :items="vehicleSearch.results"
        :placeholder="getPlaceholderResponsiveText('Please select', isMobile)"
        data-test="vehicle_confirm"
        return-object
      />

      <!-- APPROVE ACTIONS -->
      <div class="mt-3 d-flex flex-column flex-sm-row flex-wrap">
        <qz-btn
          class="text-decoration-underline mr-2"
          color="primary"
          data-test="not_my_car"
          text
          x-large
          @click.prevent="declineVehicle()"
        >
          <span>This is not my {{ vehicleTypeLabel }}</span>
        </qz-btn>

        <qz-btn
          v-if="vehicleSearch.results.length === 1 || (vehicleSearch.results.length > 1 && vehicleSearch.chosenResult)"
          class="primary--text"
          color="white"
          data-test="confirm_my_car"
          light
          outlined
          x-large
          @click.prevent="confirmVehicle"
        >
          <v-icon
            dark
            left
            size="22"
          >
            mdi-check-circle
          </v-icon>
          <span>Yes, this is my {{ vehicleTypeLabel }}</span>
        </qz-btn>
      </div>
    </form-row>
  </div>
</template>
<script>
import { cloneDeep } from 'lodash';
import {
  getPlaceholderResponsiveText,
} from '@/helpers/common';
import { UtcDate } from '@/helpers/date';
import * as commonConstants from '@/constants/common';
import { inject } from '@vue/composition-api';
import VehicleSelectorPreview from '@/components/VehicleSelector/VehicleSelectorPreview';

const MANUAL_SEARCH_TEMPLATE = {
  value: null,
  search: '',
  items: [],
  loading: false,
};

export default {
  name: 'vehicle-selector',
  components: {
    VehicleSelectorPreview,
  },
  props: {
    fields: {
      type: Array,
      default: null,
    },
    stepName: {
      type: String,
      default: 'vehicle',
    },
    vehicleTypesToAccept: {
      type: Array,
      default () { return []; },
    },
    vehicleType: {
      required: true,
      validator: value => Object.keys(commonConstants.VEHICLE_SYSTEM_TYPES).includes(value),
    },
    regNumberRules: {
      required: true,
      type: [String, Object],
    },
    initialRegNumber: {
      type: [String, Number],
      default: '',
    },
    vehicleTypeLabel: {
      type: String,
      default: '',
    },
    showRegistrationNumber: {
      type: Boolean,
      default: true,
    },
    helpText: {
      type: [String, Array, Object],
      default: null,
    },
    title: {
      type: String,
      default: '',
    },
    formObserver: {
      type: Object,
      required: true,
    },
  },
  inject: [
    'config',
    'inputCharsLimiters',
    'constants',
    'productName',
  ],
  setup () {
    const abTastyControllerUI = inject('abTastyControllerUI');

    return {
      ...abTastyControllerUI,
      getPlaceholderResponsiveText,
    };
  },
  data () {
    return {
      vehicle: {},

      isManual: false,
      isLoading: false,

      regNumber: this.initialRegNumber || '',

      vehicleSearch: {
        results: [],
        chosenResult: null,
        resultsRegNumber: null,

        type: null,
        make: cloneDeep(MANUAL_SEARCH_TEMPLATE),
        model: cloneDeep(MANUAL_SEARCH_TEMPLATE),
        year: cloneDeep(MANUAL_SEARCH_TEMPLATE),
        engine: cloneDeep(MANUAL_SEARCH_TEMPLATE),
        confirm: cloneDeep(MANUAL_SEARCH_TEMPLATE),
      },
    };
  },
  computed: {
    helpTextValue () {
      if (this.helpText) {
        return this.helpText.value || this.helpText;
      }
      return '';
    },
    isMobile () { return this.$vuetify.breakpoint.smAndDown; },
    isVehicleSearchVisible () {
      if (this.showRegistrationNumber) {
        return !this.showApprove;
      }
      return !this.isLoading && !this.showApprove;
    },
    manualSearchSystemTypes () {
      const types = [];

      if (['taxi', 'minibus'].includes(this.vehicleType)) {
        types.push(this.constants.VEHICLE_SYSTEM_TYPES.taxi);
        types.push(this.constants.VEHICLE_SYSTEM_TYPES.car);
        types.push(this.constants.VEHICLE_SYSTEM_TYPES.van);
        return types;
      }
      if (this.vehicleType === 'cateringvan') {
        types.push(
          this.constants.VEHICLE_SYSTEM_TYPES.car,
          this.constants.VEHICLE_SYSTEM_TYPES.van,
        );
        return types;
      }

      types.push(this.constants.VEHICLE_SYSTEM_TYPES[this.vehicleType]);
      return types;
    },
    makeSystemIds () {
      if (this.vehicleSearch.make.value === null) return null;
      const item = this.vehicleSearch.make.items.find((item) => item.make_key === this.vehicleSearch.make.value);

      return item?.systemIds;
    },
    showModelSelect () {
      return this.vehicleSearch.make.value && this.vehicleSearch.model.items && this.vehicleSearch.model.items.length;
    },
    showYearSelect () {
      return this.vehicleSearch.model.value && this.vehicleSearch.year.items && this.vehicleSearch.year.items.length;
    },
    showEngineSelect () {
      return this.vehicleSearch.year.value && this.vehicleSearch.engine.items && this.vehicleSearch.engine.items.length;
    },
    showConfirmVehicle () {
      return this.vehicleSearch.engine.value && this.vehicleSearch.confirm.items && this.vehicleSearch.confirm.items.length;
    },
    showApprove () {
      return !this.isManual &&
        this.vehicleSearch.results &&
        this.vehicleSearch.results.length > 0 &&
        this.vehicleSearch.resultsRegNumber;
    },
    showSingleApprove () { return this.vehicleSearch.results.length === 1; },
    approveLabel () {
      return this.vehicleSearch.results.length === 1 ? `Is this your ${this.vehicleTypeLabel}?` : `Choose your ${this.vehicleTypeLabel}`;
    },
    vehicleIcon () {
      switch (this.vehicleType) {
        case 'van':
        case 'cateringvan':
          return 'mdi-van-utility';
        case 'bike':
        case 'scooter / motorcycle':
          return 'mdi-motorbike';
        case 'taxi':
          return 'mdi-taxi';
        case 'minibus':
          return 'mdi-bus-school';
        case 'motorhome':
          return 'mdi-rv-truck';
        case 'car':
        default:
          return 'mdi-car';
      }
    },
    vehiclePlaceholder () {
      if (this.constants.PARTNERS.CompareNI.includes(this.config.partner) && this.vehicleType === 'car') {
        return 'e.g. RFZ 8272';
      }

      switch (this.vehicleType) {
        case 'van':
        case 'motorhome':
          return 'e.g. SK05KRJ';
        case 'bike':
        case 'scooter / motorcycle':
          return 'e.g. G473DAR';
        case 'car':
          return this.config.partner === 'newCniwlResponsive'
            ? 'e.g. RFZ 8272'
            : 'e.g. SN63 JKU';
        default:
          return 'e.g. SN63 JKU';
      }
    },
    makeTitle () {
      return this.constants.MAKE_TITLE || `What make is the ${this.vehicleTypeLabel}?`;
    },
  },
  watch: {
    isManual: async function (val) {
      if (!val) return;
      this.regNumber = '';
      this.vehicleSearch.results = [];
      this.vehicleSearch.resultsRegNumber = null;
      await this.$nextTick();
      this.$refs.vehicleMake.isMenuActive = true;
    },
    'vehicleSearch.chosenResult': async function (val) {
      if (!val) return;

      await this.$nextTick();
      this.$scrollTo('#confirm-vehicle', 300);
    },
    vehicleType: async function () {
      this.regNumber = '';
      this.vehicleSearch.results = [];
      this.vehicleSearch.resultsRegNumber = null;
      await this.resetVehicleSearch(['make', 'model', 'year', 'engine', 'confirm']);

      await this.declineVehicle(false);
    },
  },
  async created () {
    if (this.initialRegNumber) {
      if (this.initialRegNumber === 'N') this.isManual = true;
      else await this.searchByReg(this.regNumber);
    }
  },
  methods: {
    onRegNumberKeyDown (event) {
      if (this.regNumber.length >= this.inputCharsLimiters.REG_NUMBER_MAX) {
        if (event.keyCode >= 48 && event.keyCode <= 90 && !event.ctrlKey && !event.altKey) {
          event.preventDefault();
        }
      }
    },
    resetVehicleSearch (fields) {
      fields.forEach(field => {
        this.vehicleSearch[field].value = null;
        this.vehicleSearch[field].items = [];
      });
    },
    handleChangeMake (val) {
      this.vehicleSearch.make.value = val.make_key;
      this.resetVehicleSearch(['model', 'year', 'engine', 'confirm']);
      this.onVehicleModelSearch();
    },
    handleChangeModel ({ id }) {
      this.vehicleSearch.model.value = id;
      this.resetVehicleSearch(['year', 'engine', 'confirm']);
      this.onVehicleYearSearch();
    },
    handleChangeYear ({ id }) {
      this.vehicleSearch.year.value = id;
      this.resetVehicleSearch(['engine', 'confirm']);
      this.onVehicleEngineSearch();
    },
    handleChangeEngine ({ id }) {
      this.vehicleSearch.engine.value = id;
      this.resetVehicleSearch(['confirm']);
      this.onVehicleConfirmSearch();
    },

    async onVehicleMakeSearch () {
      const make = this.vehicleSearch.make;
      if (make.items.length > 0) return;
      if (make.loading) return;

      make.loading = true;
      const response = await this.$api.searchVehicle(this.manualSearchSystemTypes, { combine: true });

      make.items = response.data;
      make.loading = false;

      this.$nextTick();

      this.$refs.vehicleMake && this.$refs.vehicleMake.$refs.autocomplete.$el.querySelector('input').focus();
    },
    async onVehicleModelSearch () {
      const model = this.vehicleSearch.model;
      if (!this.vehicleSearch.make.value) return;
      if (model.items.length > 0) return;
      if (model.loading) return;

      model.loading = true;

      const response = await this.$api.searchVehicle(
        this.makeSystemIds,
        { makeKey: this.vehicleSearch.make.value },
      );
      const models = response.data;

      model.items = Object.keys(models).map(key => ({
        id: key,
        label: key,
      }));
      model.loading = false;

      await this.$nextTick();

      this.$refs.vehicleModel && this.$refs.vehicleModel.$refs.autocomplete.$el.querySelector('input').focus();
      this.$refs.vehicleModel.$refs.autocomplete.isMenuActive = true;
    },
    async onVehicleYearSearch () {
      const year = this.vehicleSearch.year;
      if (!this.vehicleSearch.model.value) return;
      if (year.items.length > 0) return;
      if (year.loading) return;

      year.loading = true;

      const response = await this.$api.searchVehicle(
        this.makeSystemIds,
        {
          makeKey: this.vehicleSearch.make.value,
          vehicleModel: this.vehicleSearch.model.value,
        },
      );
      const years = response.data;

      year.items = Object.keys(years).sort().reverse().map(key => ({
        id: key,
        label: key,
      }));
      year.loading = false;

      await this.$nextTick();

      this.$refs.vehicleYear && this.$refs.vehicleYear.$refs.autocomplete.$el.querySelector('input').focus();
      this.$refs.vehicleYear.$refs.autocomplete.isMenuActive = true;
    },
    async onVehicleEngineSearch () {
      const engine = this.vehicleSearch.engine;
      if (!this.vehicleSearch.year.value) return;
      if (engine.items.length > 0) return;
      if (engine.loading) return;

      engine.loading = true;

      const response = await this.$api.searchVehicle(
        this.makeSystemIds,
        {
          makeKey: this.vehicleSearch.make.value,
          vehicleModel: this.vehicleSearch.model.value,
          vehicleYear: this.vehicleSearch.year.value,
        },
      );
      const engines = response.data;

      engine.items = Object.keys(engines).map(key => ({
        id: key,
        label: engines[key],
      }));
      engine.loading = false;

      await this.$nextTick();

      this.$refs.vehicleEngine && this.$refs.vehicleEngine.$refs.autocomplete.$el.querySelector('input').focus();
      this.$refs.vehicleEngine.$refs.autocomplete.isMenuActive = true;
    },
    async onVehicleConfirmSearch () {
      const confirm = this.vehicleSearch.confirm;
      if (!this.vehicleSearch.engine.value) return;
      if (confirm.items.length > 0) return;
      if (confirm.loading) return;

      confirm.loading = true;

      const response = await this.$api.searchVehicle(
        this.makeSystemIds,
        {
          makeKey: this.vehicleSearch.make.value,
          vehicleModel: this.vehicleSearch.model.value,
          vehicleYear: this.vehicleSearch.year.value,
          vehicleEngine: this.vehicleSearch.engine.value,
        },
      );
      const confirms = response.data;

      confirm.items = Object.keys(confirms).map(key => ({
        id: key,
        label: confirms[key],
      }));
      confirm.loading = false;

      await this.$nextTick();

      this.$refs.vehicleConfirm && this.$refs.vehicleConfirm.$refs.autocomplete.$el.querySelector('input').focus();
      this.$refs.vehicleConfirm.$refs.autocomplete.isMenuActive = true;
    },

    async onVehicleConfirmSelect ({ id }) {
      this.$emit('loading', true);
      try {
        const response = await this.$api.searchVehicle(
          this.makeSystemIds,
          { abicode: id },
        );

        if (response.status === 'success') {
          const { data } = response;

          const yearOfManufacture = (this.vehicleSearch.year.value || data.manufacture_from || data.manufacture_to).toString();

          const vehicle = {
            vehicleConfirm: data.abicode,
            vehicleMake: data.make_key,
            vehicleMakeLabel: data.make_description,
            vehicleModel: data.model_description,
            vehicleModelLabel: data.model_description_full,
            engineSize: (() => {
              if (data.engine_cc && data.engine_type) return `${data.engine_cc} ${data.engine_type}`;
              return this.vehicleSearch.engine.value;
            })(),
            yearOfManufacture,
            registrationDate: `${yearOfManufacture}-01-01`,
            vehicleGreyOrImport: (() => {
              const value = +data.is_imported;
              if (['van', 'bike', 'scooter / motorcycle'].includes(this.vehicleType)) {
                return !!(isNaN(value) ? 0 : value);
              }
              return (isNaN(value) ? 0 : value) ? 'Y' : 'N';
            })(),
            vehicleRegistrationNumber: null,
          };

          if (!['scooter / motorcycle', 'bike'].includes(this.vehicleType)) {
            vehicle.vehicleAlarm = data.security_status ? 'M' : 'N';
          }

          if (['car', 'van', 'minibus', 'motorhome'].includes(this.vehicleType)) {
            vehicle.seatsOfVehicle = data.seats && this.constants.VEHICLE_SEATS_QTY.includes(+data.seats) ? +data.seats : null;
            vehicle.vehicleSide = !!data.left_hand_drive;
            vehicle.transmission = data.transmission_type || null;
          }
          if (['car', 'van', 'taxi'].includes(this.vehicleType)) {
            vehicle.doors = data.doors || null;
          }
          if (['van', 'motorhome'].includes(this.vehicleType)) {
            vehicle.payload = data.payload || null;
            vehicle.vehicleWeight = data.weight ? +data.weight : null;
            vehicle.fuelType = data.engine_type ? data.engine_type[0] || null : null;
          }

          if (['scooter / motorcycle', 'bike'].includes(this.vehicleType)) {
            vehicle.transmission = data.transmission_type || null;
          }

          this.$emit('prefill-vehicle-bought', null);
          this.$emit('prefill-estimated-value', null);

          // reset search
          ['make', 'model', 'year', 'engine', 'confirm'].forEach(field => {
            this.vehicleSearch[field].value = null;
            this.vehicleSearch[field].search = '';
            this.vehicleSearch[field].items = [];
          });
          this.$emit('select-vehicle', vehicle);
        }
      } catch (error) {
        console.error(error.toString());
      }

      this.$emit('loading', false);
    },

    async confirmVehicle () {
      const chosenResult = this.vehicleSearch.chosenResult;
      const regNumber = this.vehicleSearch.resultsRegNumber;

      this.isManual = false;

      if (!chosenResult) {
        this.$emit('toast', {
          type: 'error',
          text: 'Please confirm your vehicle.',
        });
        return;
      }

      const {
        confirmData,
        information,
        security,
        valuations = [],
      } = chosenResult;

      const yearOfManufacture = (confirmData.manufactureYear.split('-')[0]).toString();

      const vehicle = {
        vehicleConfirm: confirmData.codeABI,
        vehicleMake: confirmData.makeKey,
        vehicleMakeLabel: confirmData.make,
        vehicleModel: confirmData.modelShort,
        vehicleModelLabel: confirmData.model,
        engineSize: `${confirmData.engineCC} ${confirmData.fuel[0]}`,
        vehicleRegistrationNumber: regNumber.toUpperCase(),
        yearOfManufacture,
        registrationDate: information.date1stRegistration || `${yearOfManufacture}-01-01`,
        vehicleGreyOrImport: (() => {
          if (typeof information.isImported === 'boolean') {
            if (['van', 'bike', 'scooter / motorcycle'].includes(this.vehicleType)) {
              return information.isImported;
            }
            return information.isImported ? 'Y' : 'N';
          }
          return null;
        })(),
        colour: information.colour || null,
      };

      if (!['scooter / motorcycle', 'bike'].includes(this.vehicleType)) {
        vehicle.vehicleAlarm = security.securityStatus ? 'M' : 'N';
      }

      if (['car', 'van', 'minibus', 'motorhome'].includes(this.vehicleType)) {
        vehicle.seatsOfVehicle = information.seats && this.constants.VEHICLE_SEATS_QTY.includes(+information.seats) ? +information.seats : null;
        vehicle.vehicleSide = typeof information.leftHandDrive === 'boolean' ? information.leftHandDrive : false;
        vehicle.doors = information.doors || null;
        vehicle.transmission = information.transmission || null;
      }
      if (['car', 'van', 'taxi'].includes(this.vehicleType)) {
        vehicle.doors = information.doors || null;
      }
      if (['van', 'motorhome'].includes(this.vehicleType)) {
        vehicle.payload = +information.payload || null;
        vehicle.vehicleWeight = information.grossWeight ? +information.grossWeight : null;
        vehicle.fuelType = confirmData.fuel ? confirmData.fuel[0] || null : null;
      }

      if (['scooter / motorcycle', 'bike'].includes(this.vehicleType)) {
        vehicle.transmission = information.transmission || null;
      }

      const maxVehicleLookupPrice = Math.max(...valuations.map(({ vehicleLookupPrice }) => vehicleLookupPrice));

      if (isFinite(maxVehicleLookupPrice) && maxVehicleLookupPrice > 0) {
        this.$emit('prefill-estimated-value', maxVehicleLookupPrice);
      }

      if (information.lastSaleDate) {
        const lastSaleDate = new UtcDate(information.lastSaleDate);
        this.$emit('prefill-vehicle-bought', lastSaleDate.isValid() ? lastSaleDate : null);
      } else {
        this.$emit('prefill-vehicle-bought', null);
      }

      // reset search
      this.vehicleSearch.results = [];
      this.vehicleSearch.chosenResult = null;
      this.regNumber = '';

      this.$emit('select-vehicle', vehicle);
    },
    async declineVehicle (setManual = true) {
      this.isManual = setManual;
      this.vehicleSearch.chosenResult = null;
      this.vehicleSearch.results = [];

      this.vehicle.vehicleConfirm = null;
      this.vehicle.vehicleMake = null;
      this.vehicle.vehicleMakeLabel = null;
      this.vehicle.vehicleModel = null;
      this.vehicle.vehicleModelLabel = null;
      this.vehicle.engineSize = null;
      this.vehicle.yearOfManufacture = null;
      this.vehicle.registrationDate = null;
      this.vehicle.vehicleRegistrationNumber = null;
      this.vehicle.estimatedValue = null;
      this.vehicle.vehicleWeight = null;
      this.vehicle.fuelType = null;
      this.vehicle.payload = null;
      this.vehicle.transmission = null;

      await this.$nextTick();

      if (!setManual) return;

      this.$scrollTo('#no-registration', 300);
    },

    // Manual callbacks
    async searchByReg (rawRegNumber = '') {
      if (!rawRegNumber) {
        this.$emit('toast', {
          type: 'info',
          text: `Please, type your registration number to perform a ${this.vehicleTypeLabel} search.`,
        });
        return false;
      }

      const regNumber = rawRegNumber.toString().replace(/ /g, '');

      this.isLoading = true;
      this.isManual = false;

      try {
        const response = await this.$api.getVehicleReg({
          vehicleData: regNumber,
          partnerName: this.config.partner,
          vehicleType: this.constants.VEHICLE_REG_TYPES[this.vehicleType],
          systemId: this.constants.VEHICLE_SYSTEM_TYPES[this.vehicleType],
          vehicleLookupPrefill: this.config.vehicleLookupPrefill,
        });

        if (response.status === 'success') {
          const { data } = response;
          const valuations = data.valuations;

          const otherVehicleTypes = new Set();

          const vehicles = data.vehicles && data.vehicles.filter(({ confirmData, information }) => {
            // broken response
            if (Object.values(confirmData).some(v => v === '')) return false;

            // correct vehicle type
            if (
              Array.from(new Set([this.vehicleType, ...this.vehicleTypesToAccept])).map(vehicleType => this.constants.VEHICLE_SYSTEM_TYPES[vehicleType]).includes(information.vehicleSystemType)
            ) {
              return true;
            } else if (
              ['taxi', 'minibus', 'cateringvan', 'breakdown'].includes(this.vehicleType) &&
                ['car', 'van', 'taxi', 'minibus', 'cateringvan', 'motorhome'].map(vehicleType => this.constants.VEHICLE_SYSTEM_TYPES[vehicleType]).includes(information.vehicleSystemType)
            ) {
              return true;
            }

            // wrong vehicle type
            otherVehicleTypes.add(information.vehicleSystemType);
            return false;
          });

          if (otherVehicleTypes.size > 0) {
            // Van dialog confirmation for car forms
            const isVanDialogConfiguredFromWhiteLabel = !!this.config.vanDialogRedirectUrl;

            const isVanDialogShownForPartners = isVanDialogConfiguredFromWhiteLabel || [
              this.constants.DEFAULT_PARTNER,
              this.constants.CNI_PARTNER,
            ].includes(this.config.partner);

            const isVanDialogShownForProducts = this.productName === 'car' && otherVehicleTypes.has(this.constants.VEHICLE_SYSTEM_TYPES.van);

            const isVanDialogShown = isVanDialogShownForPartners && isVanDialogShownForProducts;

            if (isVanDialogShown) {
              let vanDialogLink = `http://www.quotezone.co.uk/SetAffiliate.php?aid=Z7F5J0-002&type=van&reg=${this.regNumber}`;

              if (isVanDialogConfiguredFromWhiteLabel) vanDialogLink = `${this.config.vanDialogRedirectUrl}&reg=${this.regNumber}`;

              if (this.config.partner === this.constants.CNI_PARTNER) vanDialogLink = `http://van-insurance.compareni.com/van/?reg=${this.regNumber}`;

              this.$emit('other-vehicle-dialog', {
                vehicleType: 'van',
                link: vanDialogLink,
              });
            }

            if (!isVanDialogShown) {
              this.isManual = true;
              this.$emit('toast', {
                type: 'error',
                text: 'We could not find the correct vehicle using your registration number. Please enter the vehicle details below.',
              });
            }
          }

          if (vehicles && vehicles.length > 0) {
            this.vehicleSearch.results = vehicles.map(vehicle => {
              return {
                ...vehicle,
                value: vehicle.confirmData.codeABI,
                valuations,
                text: this.formatVehicleText(vehicle),
              };
            });
            this.vehicleSearch.chosenResult = vehicles.length === 1 ? this.vehicleSearch.results[0] : null;
            this.vehicleSearch.resultsRegNumber = rawRegNumber.toString().replace(/ +/g, ' ');
          } else {
            if (otherVehicleTypes.size === 0) {
              this.isManual = true;
              this.$emit('toast', {
                type: 'error',
                text: 'We could not find the correct vehicle using your registration number. Please enter the vehicle details below.',
              });
            }
          }
        }
      } catch (error) {
        this.$emit('toast', {
          type: 'error',
          text: 'We could not find the correct vehicle using your registration number. Please enter the vehicle details below.',
        });
      }
      this.isLoading = false;
    },
    formatVehicleText (vehicle) {
      const text = [
        [
          vehicle.confirmData.make,
          vehicle.confirmData.model,
          vehicle.confirmData.manufactureYear,
        ].filter(s => !!s).join(', '),
        [
          this.fields.includes('engineSize') ? `${vehicle.confirmData.engineCC} cc ${this.constants.VEHICLE_FUEL_TYPES[vehicle.confirmData.fuel]}` : null,
          (vehicle.information.doors && this.fields.includes('doors')) ? `${vehicle.information.doors} Door` : null,
          (vehicle.information.transmission && this.fields.includes('transmission')) ? this.constants.VEHICLE_TRANSMISSION_TYPES[vehicle.information.transmission] : null,
          (vehicle.information.grossWeight && this.fields.includes('vehicleWeight')) ? `Gross Weight ${vehicle.information.grossWeight} kg` : null,
        ].filter(s => !!s).join(', '),
      ];

      if (this.showRegistrationNumber && this.regNumber) {
        text.unshift(this.regNumber.toUpperCase());
      }

      return text;
    },
  },
};
</script>
<style lang="scss" scoped>
.input-vehicle-search {
  &::v-deep input {
    font-family: UKNumberPlate, sans-serif;
    font-size: 24px;
  }

  &::v-deep .v-input__slot {
    background-color: rgba(var(--v-secondary-rgb, $clr-secondary-rgb), 0.3) !important;
  }
}

.btn-find-vehicle {
  min-width: 169px !important;
}
</style>
